import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlassCard, LogoLockup, EnforcedText } from '../components/shared';
import LoadingSpinner from '../components/LoadingSpinner';
import { ArrowLeft, ExternalLink, Copy, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkbookFeature = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feature, setFeature] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [glossaryTerms, setGlossaryTerms] = useState([]);

  useEffect(() => {
    fetchFeature();
    fetchGlossary();
  }, [key]);

  const fetchFeature = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/workbook/feature/${key}`);
      const data = await response.json();
      setFeature(data);
    } catch (error) {
      console.error('Error fetching feature:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGlossary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/glossary`);
      const data = await response.json();
      setGlossaryTerms(data.terms || []);
    } catch (error) {
      console.error('Error fetching glossary:', error);
    }
  };

  const handleCopyAPIRef = (apiRef) => {
    navigator.clipboard.writeText(apiRef);
    alert('API reference copied!');
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading feature details..." />;
  }

  if (!feature) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Feature not found</p>
        <button onClick={() => navigate('/workbook')} className="mt-4 text-brand-purple hover:underline">
          ← Back to Workbook
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'data', label: 'Data & Use' },
    { id: 'sources', label: 'Sources & Backend Rules' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/workbook')}
            className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <LogoLockup size="sm" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{feature.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              <EnforcedText>{feature.category} • {feature.owner}</EnforcedText>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
            feature.status === 'Green' ? 'bg-green-100 text-green-700' :
            feature.status === 'Amber' ? 'bg-orange-100 text-orange-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {feature.status}
          </span>
          {feature.links?.deep_route && (
            <button
              onClick={() => navigate(feature.links.deep_route)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand hover:opacity-90 rounded-2xl transition-opacity flex items-center gap-2 shadow-sm"
            >
              Open Live Screen
              <ExternalLink size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <GlassCard
        title={feature.name}
        subtitle={`${feature.category} Feature Documentation`}
        footer={`Definition: Feature documentation • Source: ${feature.owner} • Updated: ${new Date(feature.last_updated_at).toLocaleDateString()}`}
      >
        <div className="border-b border-gray-200 mb-4">
          <div className="flex gap-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-purple text-brand-purple'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Overview</h3>
                <p className="text-gray-700">{feature.summary_md}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-2xl">
                  <div className="text-sm font-semibold text-purple-900 mb-1">Category</div>
                  <div className="text-purple-700">{feature.category}</div>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-2xl">
                  <div className="text-sm font-semibold text-green-900 mb-1">Owner</div>
                  <div className="text-green-700">{feature.owner}</div>
                </div>
              </div>

              {feature.tags && feature.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {feature.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'data' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Data & Use Cases</h3>
                <div className="prose prose-sm max-w-none text-gray-700">
                  {feature.data_and_use_md ? (
                    <div dangerouslySetInnerHTML={{ __html: feature.data_and_use_md.replace(/\n/g, '<br/>') }} />
                  ) : (
                    <p>No data documentation available.</p>
                  )}
                </div>
              </div>

              {feature.links?.api_refs && feature.links.api_refs.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">API References</h3>
                  <div className="space-y-2">
                    {feature.links.api_refs.map((apiRef, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-2xl">
                        <code className="text-sm text-blue-900 font-mono">{apiRef}</code>
                        <button
                          onClick={() => handleCopyAPIRef(apiRef)}
                          className="p-1 hover:bg-blue-100 rounded transition-colors"
                        >
                          <Copy size={14} className="text-blue-700" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sources' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Sources & Backend Rules</h3>
                <div className="prose prose-sm max-w-none text-gray-700">
                  {feature.sources_and_rules_md ? (
                    <div dangerouslySetInnerHTML={{ __html: feature.sources_and_rules_md.replace(/\n/g, '<br/>') }} />
                  ) : (
                    <p>No source documentation available.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Glossary Inline Help */}
      {glossaryTerms.length > 0 && (
        <GlassCard
          title="Related Glossary Terms"
          subtitle="Quick reference definitions"
          footer="Definition: Glossary terms • Source: Master Workbook • Updated: Live"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {glossaryTerms.slice(0, 6).map(term => (
              <div key={term.id} className="p-3 bg-gray-50 border border-gray-200 rounded-2xl">
                <div className="flex items-center gap-2 mb-1">
                  <Info size={14} className="text-brand-purple" />
                  <span className="text-sm font-semibold text-gray-900">{term.term}</span>
                </div>
                <p className="text-xs text-gray-600 line-clamp-2">{term.definition}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default WorkbookFeature;
